import { useEffect, useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { WizardLegend } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";

import { usePlaybookScoringContext } from "../../hooks/usePlaybookScoringContext";
import { usePlaybookSignalSource } from "../../hooks/usePlaybookSignalSource";
import { usePlaybooksTrackingService } from "../../hooks/usePlaybooksTrackingService";
import { PLAYBOOK_STEP_INDEX, PLAYBOOK_WIZARD_STEPS, usePlaybookWizard } from "../../hooks/usePlaybookWizard";
import { usePlaybookWizardFlow } from "../../hooks/usePlaybookWizardFlow";
import { usePlaybookWizardState } from "../../hooks/usePlaybookWizardState";
import type { Playbook } from "../../types/playbooks.types";
import type { PlaybookWizardMode } from "../../types/playbookWizard.types";
import { PlaybookCadencePicker } from "../PlaybookCadencePicker/PlaybookCadencePicker";

import { PlaybookWizardFooter } from "./PlaybookWizardFooter";
import { PlaybookEmployeeProfilesStep } from "./steps/PlaybookEmployeeProfilesStep/PlaybookEmployeeProfilesStep";
import { PlaybookScoreCriteriaStep } from "./steps/PlaybookScoreCriteriaStep/PlaybookScoreCriteriaStep";
import { PlaybookSummaryStep } from "./steps/PlaybookSummaryStep/PlaybookSummaryStep";
import { StyledPlaybookWizardContent, StyledPlaybookWizardLegend } from "./styles";
import { UnsavedChangesModal } from "./UnsavedChangesModal";

export type { PlaybookWizardMode } from "../../types/playbookWizard.types";

type PlaybookWizardProps = {
    mode: PlaybookWizardMode;
    playbook?: Playbook;
    initialIsSharedWithAccount?: boolean;
    initialStep?: number;
    onDone: () => void;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isBootstrapping: boolean;
};

export const PlaybookWizard = ({
    mode,
    playbook,
    initialIsSharedWithAccount = false,
    initialStep = 0,
    onDone,
    filtersList,
    isBootstrapping,
}: PlaybookWizardProps) => {
    const state = usePlaybookWizardState({ mode, playbook, initialIsSharedWithAccount });
    const { prefillName } = state;
    usePlaybookSignalSource(state.isSharedWithAccount);
    const { isScoringRuleValid, scoreCriteriaLabel, selectedRuleScope, selectedRule } = usePlaybookScoringContext(
        state.isSharedWithAccount,
        state.scoringRuleId,
    );

    const wizard = usePlaybookWizard({
        name: state.name,
        selectedCadenceId: state.selectedCadenceId,
        isScoringRuleValid,
        initialStep,
    });
    const flow = usePlaybookWizardFlow({ mode, playbook, onDone, state, wizard });
    const contentRef = useRef<HTMLDivElement>(null);
    const playbooksTracking = usePlaybooksTrackingService();

    const handleLegendStepClick = (targetIndex: number) => {
        if (wizard.moveToStep(targetIndex)) {
            playbooksTracking.trackLegendStepClick(PLAYBOOK_WIZARD_STEPS[targetIndex]);
        }
    };

    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0 });
    }, [wizard.activeStep]);

    useEffect(() => {
        const ruleName = selectedRule?.businessContext?.translation;
        if (ruleName) {
            prefillName(ruleName);
        }
    }, [selectedRule?.businessContext?.translation, prefillName]);

    const renderStep = () => {
        switch (wizard.activeStep) {
            case PLAYBOOK_STEP_INDEX.scoreCriteria:
                return (
                    <PlaybookScoreCriteriaStep
                        isSharedWithAccount={state.isSharedWithAccount}
                        scoringRuleId={state.scoringRuleId}
                        onScoringRuleChange={state.setScoringRuleId}
                        filtersList={filtersList}
                        isBootstrapping={isBootstrapping}
                        currentPlaybookId={playbook?.playbookId}
                    />
                );
            case PLAYBOOK_STEP_INDEX.employeeProfiles:
                return (
                    <PlaybookEmployeeProfilesStep
                        isSharedWithAccount={state.isSharedWithAccount}
                        enabledProfileIds={state.enabledProfileIds}
                        contactsCount={state.contactsCount}
                        onContactsCountChange={state.setContactsCount}
                        employeeLocation={state.employeeLocation}
                        onEmployeeLocationChange={state.setEmployeeLocation}
                        onToggle={state.toggleProfile}
                        onProfileCreated={state.addProfile}
                        onProfileDeleted={state.removeProfile}
                    />
                );
            case PLAYBOOK_STEP_INDEX.cadence:
                return (
                    <Stack
                        gap="24px"
                        data-automation="playbook-wizard-cadence-step"
                        height="100%"
                    >
                        <PlaybookCadencePicker
                            isSharedWithAccount={state.isSharedWithAccount}
                            enabled={wizard.isCadenceStepActive}
                            onReconnectSuccess={state.handleCadenceReconnectSuccess}
                            selectedCadenceId={state.selectedCadenceId}
                            onSelectCadence={state.setSelectedCadence}
                            emailPersonalization={state.emailPersonalization}
                            onEmailPersonalizationChange={state.setEmailPersonalization}
                        />
                    </Stack>
                );
            case PLAYBOOK_STEP_INDEX.summary:
                return (
                    <PlaybookSummaryStep
                        name={state.name}
                        description={state.description}
                        onNameChange={state.setName}
                        onDescriptionChange={state.setDescription}
                        isSharedWithAccount={state.isSharedWithAccount}
                        scoreCriteriaLabel={scoreCriteriaLabel}
                        selectedRuleScope={selectedRuleScope}
                        enabledProfileIds={state.enabledProfileIds}
                        contactsCount={state.contactsCount}
                        employeeLocation={state.employeeLocation}
                        selectedCadence={state.selectedCadence ?? undefined}
                        emailPersonalization={state.emailPersonalization}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Stack
            flex="1"
            minHeight="0"
            width="100%"
            data-automation="playbook-wizard-page"
        >
            <StyledPlaybookWizardLegend
                width="100%"
                margin="24px auto 0"
                maxWidth="800px"
            >
                <WizardLegend
                    items={wizard.legendItems}
                    showTooltips
                    onItemClickFunc={handleLegendStepClick}
                />
            </StyledPlaybookWizardLegend>
            <StyledPlaybookWizardContent
                ref={contentRef}
                flex="1"
                minHeight="0"
                overflow="auto"
                width="100%"
                boxSizing="border-box"
                margin="14px auto 0"
                padding="32px"
                borderRadius="6px 6px 0 0"
                background={colorsPalettes.carbon["0"]}
                boxShadow="0px 3px 6px 0px rgba(14, 30, 62, 0.08)"
            >
                {renderStep()}
            </StyledPlaybookWizardContent>
            <PlaybookWizardFooter
                onCancel={flow.handleCancelClick}
                onBack={flow.handleBack}
                onPrimary={flow.handlePrimaryClick}
                isFirstStep={wizard.isFirstStep}
                isLastStep={wizard.isLastStep}
                isCurrentStepValid={wizard.isCurrentStepValid}
                isSubmitting={flow.isSubmitting}
                primaryLabel={flow.primaryLabel}
                primaryDisabledTooltip={wizard.primaryDisabledTooltip}
            />
            <UnsavedChangesModal
                isOpen={flow.isUnsavedChangesModalOpen}
                onCancel={flow.handleStayOnPage}
                onConfirm={flow.handleConfirmLeave}
            />
        </Stack>
    );
};
